import { useEffect, useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { RefreshCw, Search, Trash2, Download } from "lucide-react";
import { toast } from "sonner";

import { api, useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fmtDate, relTime } from "@/pages/admin/dates";

/** Format a row into a safe CSV cell — doubles up quotes and wraps if needed. */
function csvCell(v) {
  if (v == null) return "";
  const s = Array.isArray(v) ? v.join("; ") : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function exportCsv(rows) {
  const header = [
    "created_at",
    "business_name",
    "contact_name",
    "email",
    "phone",
    "city",
    "categories",
  ];
  const lines = [header.join(",")];
  rows.forEach((r) => {
    lines.push(
      [
        r.created_at,
        r.business_name,
        r.contact_name,
        r.email,
        r.phone,
        r.city,
        r.categories,
      ]
        .map(csvCell)
        .join(",")
    );
  });
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `business-leads-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function AdminBusinessLeads() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/business-leads?limit=500");
      setRows(Array.isArray(data) ? data : []);
    } catch {
      setError("Failed to load business leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    load();
  }, [user]);

  const remove = async (id, email) => {
    if (!window.confirm(`Delete business lead for ${email}? This can't be undone.`)) return;
    setBusyId(id);
    try {
      await api.delete(`/admin/business-leads/${id}`);
      setRows((rs) => rs.filter((r) => r.id !== id));
      toast.success("Lead removed.");
    } catch {
      toast.error("Could not delete lead.");
    } finally {
      setBusyId(null);
    }
  };

  const cities = useMemo(
    () => Array.from(new Set(rows.map((r) => r.city).filter(Boolean))).sort(),
    [rows]
  );

  const filtered = useMemo(() => {
    let out = rows;
    if (cityFilter) out = out.filter((r) => r.city === cityFilter);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      out = out.filter((r) =>
        [
          r.business_name,
          r.contact_name,
          r.email,
          r.phone,
          r.city,
          (r.categories || []).join(" "),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(needle)
      );
    }
    return out;
  }, [rows, q, cityFilter]);

  if (user === null) return null;
  if (!user) return <Navigate to="/login" replace state={{ from: "/admin/business-leads" }} />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-[#F7F5F2] px-6 py-10 text-[#2A2724]">
      <div className="mx-auto max-w-6xl">
        <div className="mb-8 flex flex-wrap items-baseline justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.28em] text-[#8B6F1F]">Admin</p>
            <h1 className="mt-1 font-serif text-4xl font-light">Business leads</h1>
            <p className="mt-1 text-sm text-[#6B6B6B]">
              {rows.length} lead{rows.length === 1 ? "" : "s"} · newest first · from the
              new /business/apply funnel
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9a9a9a]" />
              <Input
                data-testid="business-leads-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Filter by name / email / phone / category"
                className="w-80 rounded-full pl-9"
              />
            </div>
            <select
              data-testid="business-leads-city-filter"
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
              className="h-9 rounded-full border border-black/10 bg-white px-3 text-sm text-[#2A2724] outline-none hover:border-gold"
            >
              <option value="">All cities</option>
              {cities.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <Button
              data-testid="business-leads-export"
              onClick={() => exportCsv(filtered)}
              variant="outline"
              disabled={filtered.length === 0}
              className="rounded-full border-black/10"
            >
              <Download className="mr-2 h-4 w-4" />
              Export CSV
            </Button>
            <Button
              data-testid="business-leads-refresh"
              onClick={load}
              variant="outline"
              className="rounded-full border-black/10"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
          </div>
        </div>

        {loading && (
          <p className="rounded-2xl border border-black/10 bg-white p-6 text-sm text-[#6B6B6B]">
            Loading…
          </p>
        )}
        {error && (
          <p
            data-testid="business-leads-error"
            className="rounded-2xl border border-[#a13a3a]/30 bg-[#F8ECEC] p-6 text-sm text-[#a13a3a]"
          >
            {error}
          </p>
        )}
        {!loading && !error && filtered.length === 0 && (
          <p
            data-testid="business-leads-empty"
            className="rounded-2xl border border-black/10 bg-white p-6 text-sm text-[#6B6B6B]"
          >
            No business leads match. They'll appear here the moment a visitor
            submits the{" "}
            <Link
              to="/business/apply"
              className="underline underline-offset-4 hover:text-[#8B6F1F]"
            >
              For Business form
            </Link>
            .
          </p>
        )}
        {!loading && !error && filtered.length > 0 && (
          <div
            data-testid="business-leads-table"
            className="overflow-hidden rounded-2xl border border-black/10 bg-white"
          >
            <table className="w-full text-sm">
              <thead className="bg-[#EDEAE4] text-left text-xs uppercase tracking-[0.14em] text-[#6B6B6B]">
                <tr>
                  <th className="px-4 py-3">When</th>
                  <th className="px-4 py-3">Business</th>
                  <th className="px-4 py-3">Contact</th>
                  <th className="px-4 py-3">City</th>
                  <th className="px-4 py-3">Categories</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr
                    key={r.id}
                    data-testid={`business-lead-row-${r.id}`}
                    className="border-t border-black/5 align-top hover:bg-[#F7F5F2]"
                  >
                    <td className="whitespace-nowrap px-4 py-3">
                      <div className="text-[#2A2724]">{fmtDate(r.created_at)}</div>
                      <div className="text-[0.68rem] uppercase tracking-[0.14em] text-[#9a9a9a]">
                        {relTime(r.created_at)}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-medium text-[#2A2724]">
                      {r.business_name}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-[#2A2724]">{r.contact_name}</div>
                      <div className="mt-0.5 flex flex-col text-xs">
                        <a
                          href={`mailto:${r.email}`}
                          className="text-[#6B6B6B] underline decoration-black/15 underline-offset-4 hover:text-[#2A2724] hover:decoration-gold"
                        >
                          {r.email}
                        </a>
                        <a
                          href={`tel:${(r.phone || "").replace(/[^+0-9]/g, "")}`}
                          className="text-[#6B6B6B] underline decoration-black/15 underline-offset-4 hover:text-[#2A2724] hover:decoration-gold"
                        >
                          {r.phone}
                        </a>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-[#2A2724]">
                      {r.city}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(r.categories || []).map((c) => (
                          <span
                            key={c}
                            className="inline-flex items-center rounded-full bg-[#EDEAE4] px-2 py-0.5 text-[0.65rem] font-medium uppercase tracking-[0.1em] text-[#2A2724]"
                          >
                            {c}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <button
                        onClick={() => remove(r.id, r.email)}
                        disabled={busyId === r.id}
                        data-testid={`business-lead-delete-${r.id}`}
                        title="Delete lead"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[#a13a3a] hover:bg-[#F8ECEC] disabled:opacity-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
